import { useState, useEffect, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Search, Plus, ChevronRight, Trash2, Pencil, Download, RefreshCw, Loader2, AlertTriangle, Paperclip, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import StatusDot from '@/components/StatusDot';
import { expirationColor } from '@/lib/permitDate';
import { LinkExistingDialog } from '@/components/LinkExistingDialog';
import { inProject, buildProjectLinkPatch } from '@/lib/projectLink';

const STATUSES = ['Active', 'Expired', 'Pending Renewal'];

const EMPTY_FORM = {
  property_id: '', project_id: '', permit_code: '', description: '', frontage: '', cost: 0,
  permit_number: '', permittee_number: '', permittee_name: '', issue_date: '', notes: '',
};

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DotPermitsTab({ lockedPropertyId, lockedProjectId }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [permits, setPermits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('All');
  const [propertyFilter, setPropertyFilter] = useState(lockedPropertyId || '');
  const [expiringSoon, setExpiringSoon] = useState(false);
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);

  // Inline row expansion state
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [refreshingId, setRefreshingId] = useState(null);
  const [detailWarning, setDetailWarning] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [docCounts, setDocCounts] = useState({});

  // Link Existing (project scope only)
  const [linkOpen, setLinkOpen] = useState(false);
  const [linking, setLinking] = useState(false);

  const fmtDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString();
  };

  const fetchPermits = () => api.get('/dot-permits').then(r => setPermits(Array.isArray(r.data) ? r.data : [])).catch(() => setPermits([]));
  const fetchSummary = () => api.get('/dot-permits/summary').then(r => setSummary(r.data)).catch(() => setSummary(null));

  useEffect(() => {
    Promise.all([fetchPermits(), fetchSummary()]).finally(() => setLoading(false));
    api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : [])).catch(() => setProperties([]));
    api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setProjects([]));
    api.get('/documents/link-counts', { params: { record_type: 'DOT Permit' } }).then(r => setDocCounts(r.data || {})).catch(() => setDocCounts({}));
  }, []);

  const lockedProp = (Array.isArray(properties) ? properties : []).find(p => p.id === lockedPropertyId);

  const toggleRow = async (p) => {
    if (expandedId === p.id) { setExpandedId(null); setDetail(null); setEditMode(false); return; }
    setExpandedId(p.id);
    setDetail(null);
    setEditMode(false);
    setDetailWarning(null);
    setDetailLoading(true);
    try {
      const r = await api.get(`/dot-permits/${p.id}`);
      setDetail(r.data);
    } catch (e) { toast.error('Could not load permit'); }
    setDetailLoading(false);
  };

  const refreshPermit = async (id) => {
    setRefreshingId(id);
    try {
      const r = await api.post(`/dot-permits/${id}/fetch`);
      setPermits(prev => prev.map(p => (p.id === id ? r.data : p)));
      if (detail && detail.id === id) { setDetail(r.data); setDetailWarning(r.data?.warning || null); }
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success('Refreshed from NYC Open Data');
      fetchSummary();
    } catch { toast.error('Could not refresh permit'); }
    setRefreshingId(null);
  };

  const refreshAllPermits = async (list) => {
    const items = Array.isArray(list) ? list : [];
    if (items.length === 0) return;
    setRefreshingAll(true);
    let done = 0;
    for (const p of items) {
      setRefreshProgress({ current: done + 1, total: items.length });
      try {
        const r = await api.post(`/dot-permits/${p.id}/fetch`);
        setPermits(prev => prev.map(x => (x.id === p.id ? r.data : x)));
      } catch { /* keep going */ }
      done++;
    }
    setRefreshingAll(false);
    setRefreshProgress(null);
    fetchSummary();
    toast.success(`Refreshed ${done} permit${done === 1 ? '' : 's'}`);
  };

  // Auto-expand a permit when arriving via ?open=<id>
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      toggleRow({ id: openId });
      setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('open'); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = () => { fetchPermits(); fetchSummary(); };

  const openAdd = () => { setAddForm({ ...EMPTY_FORM, property_id: lockedPropertyId || '', project_id: lockedProjectId || '' }); setAddOpen(true); };

  // Candidates for "Link Existing": this property's permits not already on this project.
  const linkCandidates = (Array.isArray(permits) ? permits : []).filter(p => p.property_id === lockedPropertyId && !inProject(p, lockedProjectId));

  const linkSelected = async (ids) => {
    setLinking(true);
    try {
      await Promise.all(ids.map(id => {
        const p = linkCandidates.find(x => x.id === id);
        return api.put(`/dot-permits/${id}`, buildProjectLinkPatch(p, lockedProjectId));
      }));
      toast.success(`Linked ${ids.length} permit${ids.length === 1 ? '' : 's'} to this project`);
      setLinkOpen(false);
      refreshAll();
    } catch (e) { toast.error('Could not link some permits'); }
    setLinking(false);
  };

  const createPermit = async () => {
    if (!addForm.property_id) return toast.error('Property is required');
    if (!addForm.permit_code.trim()) return toast.error('Permit code is required');
    if (!addForm.description.trim()) return toast.error('Description is required');
    setSaving(true);
    try {
      await api.post('/dot-permits', { ...addForm, project_id: addForm.project_id || null });
      toast.success('Permit added');
      setAddOpen(false);
      setAddForm(EMPTY_FORM);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding permit'); }
    setSaving(false);
  };

  const startEdit = () => {
    setEditForm({
      property_id: detail.property_id || '', project_id: detail.project_id || '',
      permit_code: detail.permit_code || '', description: detail.description || '',
      frontage: detail.frontage || '', cost: detail.cost || 0,
      permit_number: detail.permit_number || '', permittee_number: detail.permittee_number || '', permittee_name: detail.permittee_name || '',
      issue_date: detail.issue_date || '', notes: detail.notes || '',
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!editForm.permit_code.trim()) return toast.error('Permit code is required');
    if (!editForm.description.trim()) return toast.error('Description is required');
    setSaving(true);
    try {
      const r = await api.put(`/dot-permits/${detail.id}`, { ...editForm, project_id: editForm.project_id || null });
      setDetail(r.data);
      setEditMode(false);
      toast.success('Permit updated');
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating permit'); }
    setSaving(false);
  };

  const deletePermit = async (id) => {
    const ok = await confirm({ title: 'Delete permit', description: 'Delete this permit? This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/dot-permits/${id}`);
      toast.success('Permit deleted');
      setExpandedId(null);
      setDetail(null);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting permit'); }
  };

  const exportPdf = async () => {
    if (!propertyFilter) return;
    setExporting(true);
    try {
      const r = await api.get(`/dot-permits/export-pdf?property_id=${propertyFilter}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const prop = (Array.isArray(properties) ? properties : []).find(p => p.id === propertyFilter);
      a.download = `DOT_Permit_Report_${(prop?.display_name || prop?.address || 'property').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error exporting PDF'); }
    setExporting(false);
  };

  const exportAllPdf = async () => {
    setExportingAll(true);
    try {
      const r = await api.get('/dot-permits/export-pdf-all', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Portfolio_DOT_Permit_Report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error exporting portfolio report'); }
    setExportingAll(false);
  };

  const filtered = (Array.isArray(permits) ? permits : []).filter(p => {
    if (lockedProjectId && !inProject(p, lockedProjectId)) return false;
    if (propertyFilter && p.property_id !== propertyFilter) return false;
    if (statusFilter !== 'All' && p.status !== statusFilter) return false;
    if (expiringSoon) {
      const c = expirationColor(p.expiration_date);
      if (!(p.status === 'Active' && (c === '#E5441A' || c === '#D97706'))) return false;
    }
    const q = search.toLowerCase();
    if (q && !(
      (p.permit_code || '').toLowerCase().includes(q) ||
      (p.permit_number || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.property_display_name || '').toLowerCase().includes(q) ||
      (p.property_address || '').toLowerCase().includes(q)
    )) return false;
    return true;
  });

  const renderFields = (form, setForm, prefix) => {
    const propertyOpts = (Array.isArray(properties) ? properties : []).map(p => ({ value: p.id, label: `${p.display_name || p.address}: ${p.borough}` }));
    const projectOpts = (Array.isArray(projects) ? projects : []).filter(p => form.property_id && p.property_id === form.property_id).map(p => ({ value: p.id, label: `${p.project_number}: ${p.name}` }));
    return (
      <div className="space-y-3">
        {lockedPropertyId ? (
          <div>
            <Label className="text-xs text-muted-foreground">Property</Label>
            <Input value={lockedProp ? (lockedProp.display_name || lockedProp.address) : 'This property'} disabled className="bg-secondary/40 border-border" data-testid={`${prefix}-property-locked`} />
          </div>
        ) : (
          <div>
            <Label className="text-xs text-muted-foreground">Property *</Label>
            <SearchableSelect options={propertyOpts} value={form.property_id} onChange={(val) => setForm({ ...form, property_id: val, project_id: '' })} placeholder="Select property" searchPlaceholder="Search properties..." emptyText="No properties found." allowClear={false} testid={`${prefix}-property`} />
          </div>
        )}
        {!lockedProjectId && (
        <div>
          <Label className="text-xs text-muted-foreground">Project <span className="text-muted-foreground/60">(optional)</span></Label>
          <SearchableSelect options={projectOpts} value={form.project_id} onChange={(val) => setForm({ ...form, project_id: val })} placeholder={form.property_id ? 'Link a project (optional)' : 'Select a property first'} searchPlaceholder="Search projects..." emptyText={form.property_id ? 'No projects linked to this property.' : 'Select a property first.'} testid={`${prefix}-project`} />
        </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Permit Code *</Label>
            <Input value={form.permit_code} onChange={e => setForm({ ...form, permit_code: e.target.value })} placeholder="e.g. 100, 201, 204" className="bg-background border-border" data-testid={`${prefix}-code`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Permit Number <span className="text-muted-foreground/60">(for live status)</span></Label>
            <Input value={form.permit_number} onChange={e => setForm({ ...form, permit_number: e.target.value })} placeholder="e.g. M012017103A55" className="bg-background border-border" style={{ fontFamily: "'DM Mono', monospace" }} data-testid={`${prefix}-permit-number`} />
          </div>
        </div>
        <div className="flex items-start gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2" data-testid={`${prefix}-status-note`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">Status and Expiration are pulled automatically from NYC DOT Open Data using the Permit Number. They cannot be set manually and stay blank until a matching permit is found.</p>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Description *</Label>
          <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Open sidewalk to install foundation" className="bg-background border-border" data-testid={`${prefix}-description`} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Frontage</Label>
            <Input value={form.frontage} onChange={e => setForm({ ...form, frontage: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-frontage`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Cost ($)</Label>
            <Input type="number" min="0" step="0.01" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-cost`} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Permittee Number <span className="text-muted-foreground/60">(contractor reg #)</span></Label>
            <Input value={form.permittee_number} onChange={e => setForm({ ...form, permittee_number: e.target.value })} placeholder="e.g. 31746" className="bg-background border-border" style={{ fontFamily: "'DM Mono', monospace" }} data-testid={`${prefix}-permittee-number`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Permittee Name</Label>
            <Input value={form.permittee_name} onChange={e => setForm({ ...form, permittee_name: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-permittee-name`} />
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Issue Date</Label>
          <Input type="date" value={form.issue_date || ''} onChange={e => setForm({ ...form, issue_date: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-issue-date`} />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Notes</Label>
          <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-background border-border" data-testid={`${prefix}-notes`} />
        </div>
      </div>
    );
  };

  const renderDetailPanel = () => {
    if (detailLoading) return <p className="text-sm text-muted-foreground py-2">Loading permit...</p>;
    if (!detail) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="border-border" disabled={refreshingId === detail.id} onClick={() => refreshPermit(detail.id)} data-testid="permit-refresh-btn">
            {refreshingId === detail.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Refresh
          </Button>
          {!editMode && <Button size="sm" variant="outline" className="border-border" onClick={startEdit} data-testid="edit-permit-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-border" onClick={() => deletePermit(detail.id)} data-testid="delete-permit-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </div>

        {editMode ? (
          <>
            {renderFields(editForm, setEditForm, 'edit-permit')}
            <div className="flex gap-2 mt-2">
              <Button onClick={saveEdit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-permit-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
            </div>
          </>
        ) : (
          <div className="space-y-4" data-testid="permit-detail-view">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {[
                ['Permit Code', detail.permit_code, true],
                ['Permit No.', detail.permit_number, true],
                ['Status', detail.status, false],
                ['Frontage', detail.frontage, false],
                ['Cost', fmtMoney(detail.cost), true],
                ['Permittee No.', detail.permittee_number, true],
                ['Permittee Name', detail.permittee_name, false],
                ['Issue Date', detail.issue_date, true],
              ].map(([label, val, mono]) => (
                <p key={label} className="text-sm">
                  <span style={{ color: '#031333' }}>{label}: </span>
                  <span style={{ color: '#010A21', fontFamily: mono ? "'DM Mono', monospace" : undefined }}>{val || '\u00A0'}</span>
                </p>
              ))}
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Expiration Date: </span>
                <span style={{ fontFamily: "'DM Mono', monospace", color: expirationColor(detail.expiration_date) || '#010A21' }}>{detail.expiration_date || '\u00A0'}</span>
              </p>
              {!lockedPropertyId && (
                <p className="text-sm">
                  <span style={{ color: '#031333' }}>Property: </span>
                  {detail.property_id
                    ? <button onClick={() => navigate(`/properties/${detail.property_id}`)} className="text-[#1B4A76] hover:underline" data-testid="permit-detail-property-link">{detail.property_display_name || detail.property_address || 'View property'}</button>
                    : <span style={{ color: '#010A21' }}>-</span>}
                </p>
              )}
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Project: </span>
                {detail.project_id
                  ? <button onClick={() => navigate(`/projects/${detail.project_id}`)} className="text-[#1B4A76] hover:underline" data-testid="permit-detail-project-link">{detail.project_number || detail.project_name || 'View project'}</button>
                  : <span style={{ color: '#010A21' }}>-</span>}
              </p>
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Documents: </span>
                {docCounts[detail.id] > 0
                  ? <button onClick={() => navigate(`/documents?record_type=${encodeURIComponent('DOT Permit')}&record_id=${detail.id}`)} className="text-[#1B4A76] hover:underline inline-flex items-center gap-1" data-testid="permit-detail-doc-count"><Paperclip className="h-3 w-3" /> {docCounts[detail.id]} linked</button>
                  : <span style={{ color: '#010A21' }}>None</span>}
              </p>
            </div>
            {[['Description', detail.description], ['Notes', detail.notes]].map(([label, val]) => (
              <div key={label}>
                <p className="text-sm" style={{ color: '#031333' }}>{label}:</p>
                <p className="text-sm whitespace-pre-wrap" style={{ color: '#010A21' }}>{val || '\u00A0'}</p>
              </div>
            ))}
            <p className="text-xs text-muted-foreground" data-testid="permit-last-fetched">
              {detail.last_fetched_at ? `Last fetched: ${fmtDate(detail.last_fetched_at)}` : 'Never refreshed'}
            </p>
            {detailWarning && (
              <div className="flex items-start gap-2 rounded-md px-3 py-2" style={{ backgroundColor: '#FEF3C7', color: '#B45309' }} data-testid="permit-refresh-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <p className="text-xs">{detailWarning}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const activeCount = summary?.total_active ?? 0;
  const exp30 = summary?.expiring_30 ?? 0;
  const lockedActive = filtered.filter(p => p.status === 'Active').length;
  const lockedExp = filtered.filter(p => { const c = expirationColor(p.expiration_date); return p.status === 'Active' && (c === '#E5441A' || c === '#D97706'); }).length;
  const lastSynced = (() => {
    const dates = (Array.isArray(permits) ? permits : []).map(p => p.last_fetched_at).filter(Boolean).sort();
    return dates.length ? dates[dates.length - 1] : null;
  })();

  return (
    <div className="space-y-4" data-testid="dot-permits-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-muted-foreground" data-testid="dot-permits-summary">{lockedPropertyId ? lockedActive : activeCount} active permits, {lockedPropertyId ? lockedExp : exp30} expiring within 30 days</p>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="permits-last-synced">
            {lastSynced ? `Last synced: ${fmtDate(lastSynced)}` : 'Never synced'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-border" disabled={refreshingAll || filtered.length === 0} onClick={() => refreshAllPermits(filtered)} data-testid="refresh-all-permits-btn">
            {refreshingAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            {refreshingAll && refreshProgress ? `Refreshing ${refreshProgress.current} of ${refreshProgress.total}...` : 'Refresh All'}
          </Button>
          {!lockedPropertyId && (
            <Button variant="outline" onClick={exportAllPdf} disabled={exportingAll} className="border-border" data-testid="export-all-permits-btn">
              <Download className="h-4 w-4 mr-1" /> {exportingAll ? 'Exporting...' : 'Export All Permits'}
            </Button>
          )}
          {propertyFilter ? (
            <Button variant="outline" onClick={exportPdf} disabled={exporting} className="border-border" data-testid="export-permits-pdf-btn">
              <Download className="h-4 w-4 mr-1" /> {exporting ? 'Exporting...' : 'Export Permit Report'}
            </Button>
          ) : (
            <span title="Filter by a property to export its permit report." data-testid="export-pdf-disabled-wrap">
              <Button variant="outline" disabled className="border-border" data-testid="export-permits-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> Export Permit Report
              </Button>
            </span>
          )}
          <Button className="bg-primary text-primary-foreground" onClick={openAdd} data-testid="add-permit-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Permit
          </Button>
          {lockedProjectId && lockedPropertyId && (
            <Button variant="outline" className="border-border" onClick={() => setLinkOpen(true)} data-testid="link-existing-permits-btn">
              <Link2 className="h-4 w-4 mr-1" /> Link Existing
            </Button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {!lockedPropertyId && (
          <div className="w-64">
            <SearchableSelect
              options={(Array.isArray(properties) ? properties : []).map(p => ({ value: p.id, label: `${p.display_name || p.address}: ${p.borough}` }))}
              value={propertyFilter}
              onChange={setPropertyFilter}
              placeholder="All Properties"
              searchPlaceholder="Search properties..."
              emptyText="No properties found."
              testid="permits-property-filter"
            />
          </div>
        )}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-card border-border text-sm" data-testid="permits-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="All" className="text-sm">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant={expiringSoon ? 'default' : 'outline'}
          onClick={() => setExpiringSoon(v => !v)}
          className={expiringSoon ? 'bg-primary text-primary-foreground' : 'border-border'}
          data-testid="permits-expiring-toggle"
        >
          Expiring Soon
        </Button>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search permits..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="permits-search" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-transparent">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Permit Code</TableHead>
              <TableHead className="text-xs text-muted-foreground">Description</TableHead>
              {!lockedPropertyId && <TableHead className="text-xs text-muted-foreground">Property</TableHead>}
              <TableHead className="text-xs text-muted-foreground">Expiration</TableHead>
              <TableHead className="text-xs text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(p => (
              <Fragment key={p.id}>
                <TableRow
                  className={`group border-border cursor-pointer ${expandedId === p.id ? 'bg-secondary/40' : 'hover:bg-secondary/30'}`}
                  onClick={() => toggleRow(p)}
                  data-testid={`permit-row-${p.id}`}
                  style={{ height: '40px' }}
                >
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>
                    <span className="inline-flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform" style={{ transform: expandedId === p.id ? 'rotate(90deg)' : 'none' }} />
                      {p.permit_code}
                    </span>
                    {docCounts[p.id] > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/documents?record_type=${encodeURIComponent('DOT Permit')}&record_id=${p.id}`); }} className="mt-0.5 ml-5 flex items-center gap-1 text-xs text-[#1B4A76] hover:underline" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid={`permit-doc-count-${p.id}`}>
                        <Paperclip className="h-3 w-3" /> {docCounts[p.id]} document{docCounts[p.id] === 1 ? '' : 's'}
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{(p.description || '').slice(0, 50)}{(p.description || '').length > 50 ? '…' : ''}</TableCell>
                  {!lockedPropertyId && (
                    <TableCell className="text-sm">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/properties/${p.property_id}`); }} className="text-[#1B4A76] hover:underline text-left" data-testid={`permit-property-link-${p.id}`}>
                        {p.property_display_name || p.property_address || '\u00A0'}
                      </button>
                    </TableCell>
                  )}
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace", color: expirationColor(p.expiration_date) }} data-testid={`permit-exp-${p.id}`}>{p.expiration_date || '\u00A0'}</TableCell>
                  <TableCell>{p.status ? <StatusDot status={p.status} /> : <span className="text-sm text-muted-foreground">-</span>}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" disabled={refreshingId === p.id} onClick={() => refreshPermit(p.id)} data-testid={`permit-refresh-${p.id}`}>
                        {refreshingId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                        <span className="ml-1">Refresh</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                <tr data-testid={`permit-expand-${p.id}`}>
                  <td colSpan={lockedPropertyId ? 5 : 6} style={{ padding: 0, border: 0 }}>
                    <div style={{ maxHeight: expandedId === p.id ? '2000px' : '0', overflow: 'hidden', transition: 'max-height 200ms ease-out' }}>
                      {expandedId === p.id && (
                        <div className="px-4 py-4 bg-secondary/20 border-b border-border" data-testid={`permit-detail-${p.id}`}>
                          {renderDetailPanel()}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              </Fragment>
            ))}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={lockedPropertyId ? 5 : 6} className="text-center text-sm text-muted-foreground py-8">No permits found.</TableCell></TableRow>
            )}
            {loading && (
              <TableRow><TableCell colSpan={lockedPropertyId ? 5 : 6} className="text-center text-sm text-muted-foreground py-8">Loading permits...</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Permit Sheet */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent className="sm:max-w-md bg-card border-border overflow-y-auto" data-testid="add-permit-sheet">
          <SheetHeader>
            <SheetTitle>Add DOT Permit</SheetTitle>
            <SheetDescription>Record a new DOT permit against a property.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {renderFields(addForm, setAddForm, 'add-permit')}
            <div className="flex gap-2 mt-5">
              <Button onClick={createPermit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="submit-add-permit">{saving ? 'Saving...' : 'Add Permit'}</Button>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <LinkExistingDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        title="Link Existing DOT Permits"
        description="Select DOT permits recorded on this property to also track under this project."
        records={linkCandidates}
        renderPrimary={(p) => p.permit_code || p.permit_number || 'Permit'}
        renderSecondary={(p) => p.description || p.permit_number || ''}
        linking={linking}
        onConfirm={linkSelected}
        emptyText="No unlinked DOT permits found for this property."
        testid="link-existing-permit"
      />
    </div>
  );
}
